// ==================== REUSABLE FRAMER MOTION ANIMATIONS ====================

import type { Variants, Transition } from 'framer-motion';

// ==================== FADE ANIMATIONS ====================

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const fadeInDown: Variants = {
  hidden: { opacity: 0, y: -30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -50 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 50 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

// ==================== SCALE ANIMATIONS ====================

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const scaleInBounce: Variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      duration: 0.5, 
      ease: [0.34, 1.56, 0.64, 1] // Back ease out for bounce effect
    }
  }
};

export const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.5, y: 20 },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: { 
      duration: 0.5, 
      ease: [0.34, 1.56, 0.64, 1]
    }
  }
};

// ==================== STAGGER CONTAINERS ====================

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

export const staggerContainerFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1
    }
  }
};

export const staggerContainerSlow: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

// ==================== TRANSITION PRESETS ====================

export const floatTransition: Transition = {
  duration: 4,
  repeat: Infinity,
  ease: [0.45, 0.05, 0.55, 0.95]
};

export const floatTransitionSlow: Transition = {
  duration: 6,
  repeat: Infinity,
  ease: [0.45, 0.05, 0.55, 0.95]
};

export const floatTransitionFast: Transition = {
  duration: 2.5,
  repeat: Infinity,
  ease: [0.45, 0.05, 0.55, 0.95]
};

export const pulseTransition: Transition = {
  duration: 2,
  repeat: Infinity,
  ease: [0.45, 0.05, 0.55, 0.95]
};

export const pulseTransitionFast: Transition = {
  duration: 1.5,
  repeat: Infinity,
  ease: [0.45, 0.05, 0.55, 0.95]
};

export const bounceTransition: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 20
};

export const smoothTransition: Transition = {
  duration: 0.3,
  ease: [0.25, 0.46, 0.45, 0.94]
};

export const springTransition: Transition = {
  type: "spring",
  stiffness: 400,
  damping: 25
};

// ==================== HOVER & TAP ANIMATIONS ====================

export const hoverScale = {
  scale: 1.05,
  transition: { duration: 0.3 }
};

export const hoverScaleLarge = {
  scale: 1.1,
  transition: { duration: 0.3 }
};

export const tapScale = {
  scale: 0.95
};

export const hoverLift = {
  y: -8,
  transition: { duration: 0.3 }
};

export const hoverLiftSmall = {
  y: -5,
  transition: { duration: 0.3 }
};

export const hoverRotate = {
  rotate: 5,
  scale: 1.1,
  transition: { duration: 0.3 }
};

// ==================== KEYFRAME ANIMATIONS ====================

export const floatKeyframes = {
  y: [-10, 10, -10]
};

export const floatKeyframesSmall = {
  y: [-5, 5, -5]
};

export const floatKeyframesLarge = {
  y: [-15, 15, -15]
};

export const pulseKeyframes = {
  scale: [1, 1.05, 1]
};

export const pulseKeyframesLarge = {
  scale: [1, 1.1, 1]
};

export const glowKeyframes = {
  scale: [1, 1.1, 1],
  opacity: [0.5, 0.7, 0.5]
};

export const shimmerKeyframes = {
  x: [-100, 100],
  opacity: [0, 1, 0]
};

// ==================== PAGE TRANSITION VARIANTS ====================

export const pageTransition: Variants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: 0.5 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.3 }
  }
};

export const pageSlideUp: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: { duration: 0.3 }
  }
};

// ==================== CARD ANIMATIONS ====================

export const cardHover: Variants = {
  rest: { 
    scale: 1,
    y: 0,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
  },
  hover: { 
    scale: 1.02,
    y: -5,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
    transition: { duration: 0.3 }
  }
};

export const floatingCard: Variants = {
  hidden: { 
    opacity: 0, 
    scale: 0, 
    y: 20 
  },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: { 
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1]
    }
  }
};

// ==================== TEXT ANIMATIONS ====================

export const textReveal: Variants = {
  hidden: { 
    opacity: 0,
    y: 20,
    clipPath: "inset(100% 0% 0% 0%)"
  },
  visible: { 
    opacity: 1,
    y: 0,
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const letterStagger: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};

export const letterAnimation: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3 }
  }
};

// ==================== BACKGROUND ANIMATIONS ====================

export const backgroundFloat = {
  x: [-20, 20, -20],
  y: [-10, 10, -10]
};

export const backgroundPulse = {
  scale: [1, 1.2, 1],
  opacity: [0.3, 0.5, 0.3]
};

export const backgroundGlow = {
  scale: [1, 1.1, 1],
  opacity: [0.5, 0.7, 0.5]
};

// ==================== LIST ITEM ANIMATIONS ====================

export const listItem: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.3 }
  }
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

export const listItemFromRight: Variants = {
  hidden: { opacity: 0, x: 10 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.3 }
  }
};

// ==================== ICON ANIMATIONS ====================

export const iconSpin: Variants = {
  hidden: { rotate: 0 },
  visible: { 
    rotate: 360,
    transition: { duration: 0.6, ease: "easeInOut" }
  }
};

export const iconBounce: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1,
    transition: { 
      type: "spring",
      stiffness: 500,
      damping: 15
    }
  }
};

// ==================== NOTIFICATION ANIMATIONS ====================

export const notificationSlide: Variants = {
  hidden: { opacity: 0, x: 100, scale: 0.8 },
  visible: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }
  },
  exit: { 
    opacity: 0, 
    x: 100, 
    scale: 0.8,
    transition: { duration: 0.3 }
  }
};

// ==================== MODAL ANIMATIONS ====================

export const modalBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

export const modalContent: Variants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }
  },
  exit: { 
    opacity: 0, 
    scale: 0.9, 
    y: 20,
    transition: { duration: 0.2 }
  }
};

// ==================== DRAWER ANIMATIONS ====================

export const drawerLeft: Variants = {
  hidden: { x: "-100%" },
  visible: { 
    x: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: { 
    x: "-100%",
    transition: { duration: 0.2 }
  }
};

export const drawerRight: Variants = {
  hidden: { x: "100%" },
  visible: { 
    x: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: { 
    x: "100%",
    transition: { duration: 0.2 }
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Creates a delayed variant for staggered animations
 */
export const createDelayedVariant = (baseVariant: Variants, delay: number): Variants => {
  const variant = { ...baseVariant };
  if (variant.visible && typeof variant.visible === 'object') {
    variant.visible = {
      ...variant.visible,
      transition: {
        ...(typeof variant.visible.transition === 'object' ? variant.visible.transition : {}),
        delay
      }
    };
  }
  return variant;
};

/**
 * Creates a custom stagger container with specified timing
 */
export const createStaggerContainer = (
  staggerChildren: number = 0.1,
  delayChildren: number = 0.2
): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren,
      delayChildren
    }
  }
});

